// ─── Route Paths ───────────────────────────────────────────────────────────────
export const ROUTE_PATHS = {
  HOME: '/',
  GARANTIA: '/garantia',
  INSTALADOR: '/instalador',
  DASHBOARD: '/dashboard',
  DASHBOARD_LEAD: '/dashboard/lead/:id',
} as const;

// ─── Types ─────────────────────────────────────────────────────────────────────
export type TipoImovel = 'predio' | 'moradia' | 'escritorio';

export type EstadoLead =
  | 'novo' | 'em_contacto' | 'orcamentos_enviados'
  | 'visita_agendada' | 'obra_em_curso' | 'concluido' | 'cancelado';

export interface Lead {
  id: string; criadoEm: string; nome: string; email: string; telefone: string;
  morada: string; localidade: string; codigoPostal: string;
  tipoImovel: TipoImovel; dataVisita: string; notas?: string;
  estado: EstadoLead;
  instaladorId?: string;   // legado
  instaladorIds?: string[];
  numeroFatura?: string;
  notasInternas?: string; dataConclusao?: string;
  // Gestão financeira
  sinalPago?: boolean;          // 30% recebido do cliente
  sinalPagoData?: string;
  pagamentoFinalPago?: boolean; // 70% recebido do cliente
  pagamentoFinalData?: string;
  instaladorPago?: boolean;     // pago ao instalador (≤48h)
  instaladorPagoData?: string;
}

export interface Instalador {
  id: string; nome: string; zona: string; telefone: string; email: string;
}

export type StatusInstalador = 'pendente' | 'aprovado' | 'rejeitado';

export interface InstaladorComelit {
  id: string; criadoEm: string; nome: string; email: string; password: string;
  telefone: string; nif: string; zona: string; morada: string;
  temSeguro: boolean; numeroClienteContera: string;
  status: StatusInstalador; notasContera?: string;
  proximaCertificacao?: string;
}

export type EstadoOrcamento = 'aberto' | 'em_selecao' | 'fechado';

export interface RespostaOrcamento {
  instaladorId: string; criadoEm: string; selecionado: boolean;
}

export interface PedidoOrcamento {
  id: string; criadoEm: string; leadId: string; zona: string;
  descricao: string; estado: EstadoOrcamento; respostas: RespostaOrcamento[];
}

export type EstadoExtensao = 'pendente' | 'aprovado' | 'rejeitado';

export interface PedidoExtensaoGarantia {
  id: string; criadoEm: string; leadId: string; motivo: string; estado: EstadoExtensao;
}

// ─── Constants ─────────────────────────────────────────────────────────────────
export const TIPO_IMOVEL_LABELS: Record<TipoImovel, string> = {
  predio: 'Prédio', moradia: 'Moradia', escritorio: 'Escritório',
};

export const ESTADO_LABELS: Record<EstadoLead, string> = {
  novo: 'Novo', em_contacto: 'Em Contacto', orcamentos_enviados: 'Orçamentos Enviados',
  visita_agendada: 'Visita Agendada', obra_em_curso: 'Obra em Curso',
  concluido: 'Concluído', cancelado: 'Cancelado',
};

export const ESTADO_COLORS: Record<EstadoLead, string> = {
  novo: 'bg-blue-100 text-blue-700 border-blue-200',
  em_contacto: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  orcamentos_enviados: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  visita_agendada: 'bg-purple-100 text-purple-700 border-purple-200',
  obra_em_curso: 'bg-orange-100 text-orange-700 border-orange-200',
  concluido: 'bg-green-100 text-green-700 border-green-200',
  cancelado: 'bg-red-100 text-red-700 border-red-200',
};

export const DASHBOARD_PASSWORD = 'contera2026';

export const INSTALADORES_INICIAIS: Instalador[] = [
  { id: '1', nome: 'João Silva', zona: 'Lisboa', telefone: '912 345 678', email: 'joao@instala.pt' },
  { id: '2', nome: 'Pedro Costa', zona: 'Porto', telefone: '923 456 789', email: 'pedro@instala.pt' },
  { id: '3', nome: 'Carlos Ferreira', zona: 'Coimbra', telefone: '934 567 890', email: 'carlos@instala.pt' },
  { id: '4', nome: 'Rui Almeida', zona: 'Braga', telefone: '945 678 901', email: 'rui@instala.pt' },
  { id: '5', nome: 'André Santos', zona: 'Setúbal', telefone: '956 789 012', email: 'andre@instala.pt' },
  { id: '6', nome: 'Miguel Rodrigues', zona: 'Faro / Algarve', telefone: '967 890 123', email: 'miguel@instala.pt' },
];

// ─── Airtable imports ──────────────────────────────────────────────────────────
import {
  TABLES, atGetAll, atGetByLid, atCreate, atUpdate, atUpdateByLid, atDeleteByLid,
  leadToFields, fieldsToLead,
  instaladorToFields, fieldsToInstalador,
  instaladorComelitToFields, fieldsToInstaladorComelit,
  pedidoOrcamentoToFields, fieldsToPedidoOrcamento,
  pedidoExtensaoToFields, fieldsToPedidoExtensao,
  Proposta, propostaToFields, fieldsToProposta,
} from './airtable';
export type { Proposta };

// ─── Leads ─────────────────────────────────────────────────────────────────────
export async function getLeads(): Promise<Lead[]> {
  const recs = await atGetAll(TABLES.LEADS);
  return recs.map((r) => fieldsToLead(r.fields)).sort(
    (a, b) => new Date(b.criadoEm).getTime() - new Date(a.criadoEm).getTime()
  );
}

export async function addLead(lead: Omit<Lead, 'id' | 'criadoEm' | 'estado'>): Promise<Lead> {
  const newLead: Lead = {
    ...lead, id: crypto.randomUUID(), criadoEm: new Date().toISOString(), estado: 'novo',
  };
  await atCreate(TABLES.LEADS, leadToFields(newLead));
  return newLead;
}

export async function updateLead(id: string, updates: Partial<Lead>): Promise<Lead | null> {
  const rec = await atGetByLid(TABLES.LEADS, id);
  if (!rec) return null;
  const current = fieldsToLead(rec.fields);
  if (updates.estado === 'concluido' && !current.dataConclusao) {
    updates.dataConclusao = new Date().toISOString();
  }
  const updated: Lead = { ...current, ...updates };
  await atUpdate(TABLES.LEADS, rec.id, leadToFields(updated));
  return updated;
}

export async function deleteLead(id: string): Promise<void> {
  await atDeleteByLid(TABLES.LEADS, id);
}

export async function getLeadByCode(code: string): Promise<Lead | null> {
  const prefix = code.replace('CTR-', '').toLowerCase();
  const leads = await getLeads();
  return leads.find((l) => l.id.toLowerCase().startsWith(prefix)) ?? null;
}

// ─── Instaladores (internos) ───────────────────────────────────────────────────
export async function getInstaladores(): Promise<Instalador[]> {
  const recs = await atGetAll(TABLES.INSTALADORES);
  return recs.map((r) => fieldsToInstalador(r.fields));
}

export async function addInstalador(ins: Omit<Instalador, 'id'>): Promise<Instalador> {
  const n: Instalador = { ...ins, id: crypto.randomUUID() };
  await atCreate(TABLES.INSTALADORES, instaladorToFields(n));
  return n;
}

export async function deleteInstalador(id: string): Promise<void> {
  await atDeleteByLid(TABLES.INSTALADORES, id);
}

// ─── Instaladores Comelit ─────────────────────────────────────────────────────
export async function getInstalaldoresComelit(): Promise<InstaladorComelit[]> {
  const recs = await atGetAll(TABLES.INSTALADORES_COMELIT);
  // Guardar o recId real do Airtable no campo id para uso directo no delete
  return recs.map((r) => {
    const obj = fieldsToInstaladorComelit(r.fields);
    obj.id = r.id; // usar sempre o recId real do Airtable
    return obj;
  });
}

export async function registerInstaladorComelit(
  data: Omit<InstaladorComelit, 'id' | 'criadoEm' | 'status'>
): Promise<InstaladorComelit> {
  const n: InstaladorComelit = {
    ...data, id: crypto.randomUUID(), criadoEm: new Date().toISOString(), status: 'pendente',
  };
  await atCreate(TABLES.INSTALADORES_COMELIT, instaladorComelitToFields(n));
  return n;
}

export async function updateInstaladorComelit(id: string, updates: Partial<InstaladorComelit>): Promise<void> {
  // id é agora o recId real do Airtable
  if (id.startsWith('rec')) {
    const TOKEN = import.meta.env.VITE_AIRTABLE_TOKEN as string;
    const BASE_ID = import.meta.env.VITE_AIRTABLE_BASE_ID as string;
    const res = await fetch(`https://api.airtable.com/v0/${BASE_ID}/${encodeURIComponent(TABLES.INSTALADORES_COMELIT)}/${id}`
      , { headers: { Authorization: `Bearer ${TOKEN}` } });
    if (!res.ok) return;
    const rec = await res.json() as { id: string; fields: Record<string,unknown> };
    const current = fieldsToInstaladorComelit(rec.fields);
    await atUpdate(TABLES.INSTALADORES_COMELIT, id, instaladorComelitToFields({ ...current, ...updates }));
  } else {
    const rec = await atGetByLid(TABLES.INSTALADORES_COMELIT, id);
    if (!rec) return;
    const current = fieldsToInstaladorComelit(rec.fields);
    await atUpdate(TABLES.INSTALADORES_COMELIT, rec.id, instaladorComelitToFields({ ...current, ...updates }));
  }
}

export async function loginInstaladorComelit(email: string, password: string): Promise<InstaladorComelit | null> {
  const all = await getInstalaldoresComelit();
  return all.find((i) => i.email === email && i.password === password) ?? null;
}

// ─── Pedidos de Orçamento ──────────────────────────────────────────────────────
export async function getPedidosOrcamento(): Promise<PedidoOrcamento[]> {
  const recs = await atGetAll(TABLES.PEDIDOS_ORCAMENTO);
  return recs.map((r) => fieldsToPedidoOrcamento(r.fields)).sort(
    (a, b) => new Date(b.criadoEm).getTime() - new Date(a.criadoEm).getTime()
  );
}

export async function createPedidoOrcamento(
  data: Omit<PedidoOrcamento, 'id' | 'criadoEm' | 'respostas' | 'estado'>
): Promise<PedidoOrcamento> {
  const n: PedidoOrcamento = {
    ...data, id: crypto.randomUUID(), criadoEm: new Date().toISOString(), estado: 'aberto', respostas: [],
  };
  await atCreate(TABLES.PEDIDOS_ORCAMENTO, pedidoOrcamentoToFields(n));
  return n;
}

export async function responderOrcamento(pedidoId: string, instaladorId: string): Promise<void> {
  const rec = await atGetByLid(TABLES.PEDIDOS_ORCAMENTO, pedidoId);
  if (!rec) return;
  const pedido = fieldsToPedidoOrcamento(rec.fields);
  if (pedido.respostas.find((r) => r.instaladorId === instaladorId)) return;
  pedido.respostas.push({ instaladorId, criadoEm: new Date().toISOString(), selecionado: false });
  pedido.respostas.forEach((r, i) => { r.selecionado = i < 3; });
  if (pedido.respostas.length >= 3) pedido.estado = 'em_selecao';
  await atUpdate(TABLES.PEDIDOS_ORCAMENTO, rec.id, pedidoOrcamentoToFields(pedido));
}

export async function updatePedidoOrcamento(id: string, updates: Partial<PedidoOrcamento>): Promise<void> {
  const rec = await atGetByLid(TABLES.PEDIDOS_ORCAMENTO, id);
  if (!rec) return;
  const current = fieldsToPedidoOrcamento(rec.fields);
  await atUpdate(TABLES.PEDIDOS_ORCAMENTO, rec.id, pedidoOrcamentoToFields({ ...current, ...updates }));
}

// ─── Pedidos de Extensão de Garantia ─────────────────────────────────────────
export async function getPedidosExtensao(): Promise<PedidoExtensaoGarantia[]> {
  const recs = await atGetAll(TABLES.PEDIDOS_EXTENSAO);
  return recs.map((r) => fieldsToPedidoExtensao(r.fields)).sort(
    (a, b) => new Date(b.criadoEm).getTime() - new Date(a.criadoEm).getTime()
  );
}

export async function createPedidoExtensao(leadId: string, motivo: string): Promise<PedidoExtensaoGarantia> {
  const n: PedidoExtensaoGarantia = {
    id: crypto.randomUUID(), criadoEm: new Date().toISOString(), leadId, motivo, estado: 'pendente',
  };
  await atCreate(TABLES.PEDIDOS_EXTENSAO, pedidoExtensaoToFields(n));
  return n;
}

export async function updateExtensao(id: string, estado: EstadoExtensao): Promise<void> {
  await atUpdateByLid(TABLES.PEDIDOS_EXTENSAO, id, { estado });
}

// ─── Helpers ───────────────────────────────────────────────────────────────────
export function formatDate(iso: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function formatDateShort(dateStr: string): string {
  if (!dateStr) return '—';
  const [y, m, d] = dateStr.split('-');
  return `${d}/${m}/${y}`;
}

export function generateLeadCode(id: string): string {
  return `CTR-${id.slice(0, 8).toUpperCase()}`;
}

export function calcGarantia(dataConclusao: string): { expiry: Date; daysLeft: number; percent: number } {
  const start = new Date(dataConclusao);
  const expiry = new Date(start);
  expiry.setFullYear(expiry.getFullYear() + 3);
  const now = new Date();
  const total = expiry.getTime() - start.getTime();
  const elapsed = now.getTime() - start.getTime();
  const daysLeft = Math.max(0, Math.round((expiry.getTime() - now.getTime()) / 86400000));
  const percent = Math.max(0, Math.min(100, Math.round((1 - elapsed / total) * 100)));
  return { expiry, daysLeft, percent };
}

export async function addProposta(p: Omit<Proposta,'id'>): Promise<Proposta> {
  const rec = await atCreate('Propostas', propostaToFields(p));
  return fieldsToProposta(rec.fields);
}

export async function getPropostas(): Promise<Proposta[]> {
  const recs = await atGetAll('Propostas');
  return recs.map(r => fieldsToProposta(r.fields));
}

export async function updateProposta(id: string, fields: Record<string,unknown>): Promise<void> {
  await atUpdateByLid('Propostas', id, fields);
}

export async function deleteInstaladorComelit(recId: string): Promise<void> {
  await fetch(`https://api.airtable.com/v0/appwjo1E73xqtaByq/InstalaldoresComelit/${recId}`, {
    method: 'DELETE',
    headers: { Authorization: 'Bearer pat2DwLHSO528SPnR.2601a87b86f858dc9215e1106f15af331514cbb3edea4521918edb21917cc8a3' },
  });
}
